import React, {useCallback, useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow,} from "@amzn/meridian/table"
import Checkbox from "@amzn/meridian/checkbox"
import Pagination from "@amzn/meridian/pagination"
import Link from "@amzn/meridian/link";
import Tag from "@amzn/meridian/tag";
import Tooltip from "@amzn/meridian/tooltip";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Loader from "@amzn/meridian/loader";
import Box from "@amzn/meridian/box";
import Modal from "@amzn/meridian/modal";
import TableScrollWrapper from "../common/TableScrollWrapper";

import {DepartContext} from "./context/DepartContext";
import {
    getRefreshInterval,
    getRegion,
    isARorSSD,
    naIfDefault, isNotINRegionOrSSD,
    isNotSSDAndINRegion,
    trimColValue
} from "../util/Util";
import {getDocumentURL} from "../handler/ParentDataHandler";
import {FormattedMessage} from "react-intl";
import {DEPART_STATUS, getDepartStatusColor} from "../util/EntityStatusManager";
import {DepartFilterContext} from "./context/DepartFilterContext";
import {getRemainingTTL, isRouteCacheValid} from "../util/CacheManager";
import {ROUTE_DATA_CACHE_KEY} from "../util/LocalStorageKeys";
import {applyInlineStagingLocationFilter} from "../util/FilterUtil";
import useCapabilityContext from "../global/context/useCapabilityContext";
import {DEFAULT_CAPABILITIES, REGION, DEPART_TABLE_ACTIONS} from "../util/Constants";
import {getStationCode} from "../network/NetworkUtil";
import {useDeviceLayout} from "../common/context/DeviceLayoutContext";
import RouteContextMenu from "./RouteContextMenu";
import Button from "@amzn/meridian/button";

const DepartTable = () => {

    const {viewModel, dispatch} = useContext(DepartContext)
    const {viewModelF, dispatchF} = useContext(DepartFilterContext)
    const {configViewModel, configViewModelRef, configDispatch} = useCapabilityContext();
    const {spacingInset_shorthand2} = useDeviceLayout()
    /** Pagination State **/
    const [currentPage, setCurrentPage] = useState(1)

    /** Sorting **/
    const [sortColumn, setSortColumn] = useState("routeSLA")
    const [sortDirection, setSortDirection] = useState("ascending")
    const onSort = useCallback(({sortColumn, sortDirection}) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    /** In Line Table Filter **/
    const [routeCodeFilter, setRouteCodeFilter] = useState("")
    const [typeFilter, setTypeFilter] = useState("")
    const [statusFilter, setStatusFilter] = useState("")
    const [slaFilter, setSLAFilter] = useState("")
    const [progressFilter, setProgressFilter] = useState("")
    const [locationFilter, setLocationFilter] = useState("")
    const [sectorsFilter, setSectorsFilter] = useState("")
    const [driverFilter, setDriverFilter] = useState("")
    const [cartsFilter, setCartsFilter] = useState("")
    const [stationCodeFilter, setStationCodeFilter] = useState("")
    const [departByTimeFilter, setDepartByTimeFilter] = useState("")
    const [checkedRoutes, setCheckedRoutes] = useState([]);
    const [buttonLoading, setButtonLoading] = useState(false);

    /** Filters **/
    const filterOption = (route, filter, key) => {
        if (viewModelF[filter].length === 0) {
            return true;
        }
        let result = false;
        viewModelF[filter].forEach(filterValue => {
            if (!route[key]
                || (filter === "status" && route[key] === filterValue)
                || (filter !== "status" && route[key].includes(filterValue))) {
                result = true;
            }
        })
        return result;
    }
    const inLineFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] && route[key].toString().toLowerCase().startsWith(filter.toLowerCase()));
    }

    const inLineContainsFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] != null && route[key].toString().toLowerCase().includes(filter.toLowerCase()));
    }
    let routeList = viewModel.routeList;
    routeList.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === "ascending" ? -1 : 1
        }
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === "ascending" ? 1 : -1
        }
        return 0
    })

    routeList = routeList
        .filter(route => filterOption(route, "cluster", "routeCode"))
        .filter(route => filterOption(route, "driverType", "routeType"))
        .filter(route => filterOption(route, "departure", "routeSLA"))
        .filter(route => filterOption(route, "status", "status"))
        .filter(route => filterOption(route, "departByTime", "departByTime"))
        .filter(route => inLineFilter(route, routeCodeFilter, "routeCode"))
        .filter(route => inLineContainsFilter(route, typeFilter, "routeType"))
        .filter(route => inLineFilter(route, statusFilter, "status"))
        .filter(route => inLineFilter(route, slaFilter, "routeSLA"))
        .filter(route => inLineFilter(route, progressFilter, "departProgress"))
        .filter(route => applyInlineStagingLocationFilter(route, locationFilter))
        .filter(route => inLineContainsFilter(route, sectorsFilter, "sectorCodes"))
        .filter(route => inLineFilter(route, driverFilter, "driver"))
        .filter(route => inLineFilter(route, cartsFilter, "totalCarts"))
        .filter(route => inLineFilter(route, stationCodeFilter, "stationCode"))
        .filter(route => inLineFilter(route, departByTimeFilter, "departByTime"))



    /** Pagination **/
    const itemOnPage = 50;
    const pageSize = Math.ceil(routeList.length / itemOnPage);
    const startPage = (currentPage  - 1) * itemOnPage;
    const endPage = (startPage + itemOnPage) > routeList.length - 1 ? routeList.length - 1 :
        (startPage + itemOnPage - 1);
    if (startPage > routeList.length) {
        setCurrentPage(1);
    }

    const getStation = (stationCode) => {
        const xptNodes = viewModelF.data.xptNodes;

        if (Array.isArray(xptNodes)
            && xptNodes.length
            && DEFAULT_CAPABILITIES.includes(configViewModel.capability)) {
            return getStationCode() !== stationCode && xptNodes.includes(stationCode) ? stationCode : "-"
        }

        return "-";
    }

    /**
     * A route can be moved to ready for dispatch if any of the following conditions match
     *    -  At least one package is stowed/topped-off in that route
     *    -  There are no delivery package in that route, meaning all packages are CReturns
     *    -  Route status is NOT_READY
     */
    const isRouteReadyForDispatch = (route) => {
        if (route.status !== DEPART_STATUS.NOT_READY) {
            return false;
        }

        if (!(route && route.stowProgress && route.stowProgress.split("/").length === 2)) {
            return false;
        }
        let [stowedPackages, totalPackages] = route.stowProgress.split("/");
        const isCReturnOnlyRoute = totalPackages === "0";
        const hasStowingStartedInRoute = stowedPackages !== "0";
        return isCReturnOnlyRoute || hasStowingStartedInRoute || route.topOffPackageCount !== 0;
    }

    const onRouteClick = (route) => {
        getDocumentURL().then(result => {
            const params = [
                "Depart",
                naIfDefault(route.routeId),
                naIfDefault(encodeURIComponent(route.routeStatus)),
                naIfDefault(encodeURIComponent(route.stagingLocation)),
                naIfDefault(route.hazmatWeight),
                naIfDefault(route.picker),
                naIfDefault(route.routeCode),
                naIfDefault(route.stationCode)
            ]
            const url = new URL(result.replace("depart", "pick"));
            const shareableLink = "/routeDetail/" + params.join("/");
            url.searchParams.set("shareableLink", shareableLink);
            if (configViewModel.stationConfig.amxl) {
                const amxlUrl = new URL(result.replace("depart", "stowv2"));
                window.open(amxlUrl.href, "_parent");
            } else window.open(url.href, "_parent")
        });
    }

    const getDepartData = () => {
        if (Array.isArray(viewModelF.xptNodes)) {
            dispatch({
                type: DEPART_TABLE_ACTIONS.LOAD_VIEW,
                data: {
                    dispatcher: dispatch,
                    filterDispatcher: dispatchF,
                    baseUrl: configViewModel.baseUrl,
                    xptNodes: viewModelF.xptNodes,
                    configDispatch: configDispatch,
                    stationConfig: configViewModel.stationConfig,
                    capability: configViewModel.capability
                }
            });
       }
    }

    /**
     * Check if all rows are checked for IN Depart
     * - If checkedRows.length is 0, then false
     * - Check route status, and check if it is present in the list
     */
    const areAllRowsChecked = (routeList) => {
        if (checkedRoutes.length === 0) {
            return false;
        }

        for (let key in routeList) {
            let route = routeList[key];
            if (isRouteReadyForDispatch(route)
                && !(checkedRoutes.includes(route.routeId))) {
                return false;
            }
        }

        return true;
    }

    /**
     * Check all filtered rows - IN Depart
     */
    const checkAllRows = (event) => {
        setCheckedRoutes(event ? routeList
            .filter(route => (isRouteReadyForDispatch(route)))
            .map(route => route.routeId): []);
    }

    /**
     * Check if this row is selected - IN Depart.
     */
    const isChecked = (routeId) => {
        return checkedRoutes.includes(routeId) === true;
    }

    /**
     * Select row for depart - IN Depart.
     */
    const checkRow = (routeId) => {
        checkedRoutes.includes(routeId) === true
            ? setCheckedRoutes(old => old.filter(row => row !== routeId))
            : setCheckedRoutes(old => [...old, routeId]);
    }

    /**
     * Close Bulk Depart Notification Modal - IN Depart.
     */
    const closeModal = () => {
        setButtonLoading(false);
        setCheckedRoutes([]);
        dispatch({
            type: DEPART_TABLE_ACTIONS.CLOSE_BULK_UPDATE_NOTIFICATION
        })
    }

    /**
     * Open Confirmation for Bulk Depart.
     */
    const handleOpenBulkDepartModal = () => {
        dispatch({
            type: DEPART_TABLE_ACTIONS.BULK_UPDATE_ROUTE_STATUS_MODAL, data: {
                bulkDepartStatus: {
                    routeIds: routeList
                        .filter(route => checkedRoutes.includes(route.routeId))
                        .map(route => ({
                            routeId: route.routeId,
                            routeCode: route.routeCode})),
                    modalOpen: true
                }
            }
        });
    };

    /**
     * Bulk Depart selected & confirmed routes - IN Depart.
     */
    const handleBulkDepart = () => {
        setButtonLoading(true);
        dispatch({
            type: DEPART_TABLE_ACTIONS.BULK_UPDATE_ROUTE_STATUS,
            data: {
                dispatcher: dispatch,
                filterDispatcher: dispatchF,
                routeIds: routeList
                    .filter(route => checkedRoutes.includes(route.routeId))
                    .map(route => ({
                        routeId: route.routeId,
                        routeCode: route.routeCode})),
                baseUrl: configViewModel.baseUrl,
                stationConfig: configViewModel.stationConfig,
                configDispatch: configDispatch,
                xptNodes: viewModelF.xptNodes
            }
        });
    }
    /**
     * This gets invoked on following events:
     *  1. On every page load,
     *  2. Every time xpt filter value is changed,
     *  Note: stationConfig can be update after GetStationRoutes API response is received.
     */
    useEffect(() => {
        getDepartData();
        let firstPollTime = getRefreshInterval(configViewModelRef.current.stationConfig);
        const remainingTTL = getRemainingTTL(ROUTE_DATA_CACHE_KEY);
        if (viewModelF.xptNodes && isRouteCacheValid(viewModelF.xptNodes)) {
            firstPollTime = remainingTTL;
        }
        const timeoutHandler = () => {
            getDepartData();
            timeout = setTimeout(timeoutHandler, getRefreshInterval(configViewModelRef.current.stationConfig));
        }
        let timeout = setTimeout(timeoutHandler, firstPollTime);

        return () => clearTimeout(timeout);
    }, [viewModelF.xptNodes]);

    return (
        <div>
            <TableScrollWrapper>
                {viewModel.bulkDepartStatus ?
                <Modal width={500} title="Depart Status"
                    open={true} onClose={closeModal}
                    scrollContainer="modal" closeLabel="Close" describedById="alert-modal">
                    {viewModel.bulkDepartStatus.modalOpen ?
                        <Row>
                            Are you sure you want to continue departing {checkedRoutes.length} routes?
                            <Button size={"small"} disabled={buttonLoading} onClick={handleBulkDepart}>{buttonLoading ? <Loader size={"small"} type={"circular"}/> : null}Confirm</Button>
                        </Row>
                        : null}
                        <Table showStripes={true}
                               fixHeaderRows={true} headerRows={1}>
                            <TableRow>
                                <TableCell>Route Code</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                            {viewModel.bulkDepartStatus.routeIds.map((v, k) => (
                                <TableRow>
                                    <TableCell>{v.routeCode}</TableCell>
                                    <TableCell>
                                        {viewModel.bulkDepartStatus.modalOpen ? <b>-</b> :
                                            viewModel.bulkDepartStatus.successIds.includes(v.routeId)
                                                ? <b>SUCCESS</b>
                                                : viewModel.bulkDepartStatus.failureIds.includes(v.routeId)
                                                    ? <b>FAILED</b>
                                                    : <Loader size={"small"} type={"circular"}/>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>
                    <Box spacingInset={"small"}/>
                </Modal> : null}
                <Row width="100%" widths="fill" spacingInset={spacingInset_shorthand2} className="enable-scroll-x">
                    <div>
                        <div>
                            {isNotSSDAndINRegion(configViewModel.capability) ?
                                <Row alignmentHorizontal={"right"}>
                                    <Button disabled={checkedRoutes.length === 0 || buttonLoading} onClick={handleOpenBulkDepartModal}>{buttonLoading ? <Loader size={"small"} type={"circular"}/> : null}Depart Routes</Button>
                                </Row>: null}
                            <Table showStripes={true}  headerColumns={1} stickyHeaderColumn={true}
                                   fixHeaderRows={true} headerRows={1} stickyHeaderRow={true}
                                   showDividers={true} spacing="small"
                                   sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort}>
                                <TableRow>
                                    {isNotINRegionOrSSD(configViewModel.capability)?
                                        < TableCell alignmentHorizontal="right" sortColumn="routeSLA">
                                        {configViewModel.stationConfig.amxl ?
                                        <FormattedMessage id="depart.stowByTime" defaultMessage="Stow by time"/> :
                                        <FormattedMessage id="stage.stageByTime" defaultMessage="Stage by time"/>}
                                        </TableCell> : <TableCell>
                                            <Checkbox checked={areAllRowsChecked(routeList)} onChange={(e) => checkAllRows(e)}/>
                                        </TableCell>
                                    }
                                    {isNotSSDAndINRegion(configViewModel.capability) ?
                                    < TableCell alignmentHorizontal="right" sortColumn="DepartByTime">
                                        <FormattedMessage id="departTable.departByTime" defaultMessage="Depart by time"/>
                                    </TableCell> : null }
                                    <TableCell sortColumn="routeCode">
                                            <FormattedMessage id="departTable.routeId" defaultMessage="Route ID"/>
                                        </TableCell>
                                    {isNotINRegionOrSSD(configViewModel.capability)?
                                        <TableCell sortColumn="stagingLocation">
                                            <FormattedMessage id="departTable.location" defaultMessage="Location"/>
                                        </TableCell> : <TableCell sortColumn="sectorCodes">
                                            <FormattedMessage id="departTable.sectors" defaultMessage="Sectors"/>
                                        </TableCell>
                                    }
                                    <TableCell sortColumn="status">
                                        <FormattedMessage id="departTable.status" defaultMessage="Status"/>
                                    </TableCell>
                                    <TableCell alignmentHorizontal="right" sortColumn="departProgress">
                                        <FormattedMessage id="departTable.progress" defaultMessage="Progress"/>
                                    </TableCell>
                                    <TableCell sortColumn="routeType">
                                        <FormattedMessage id="departTable.type" defaultMessage="Type"/>
                                    </TableCell>
                                    <TableCell sortColumn="driver">
                                        <FormattedMessage id="departTable.driver" defaultMessage="Driver"/>
                                    </TableCell>
                                    {!(isARorSSD(configViewModelRef.current.capability) || getRegion() === REGION.IN) && !configViewModel.stationConfig.amxl ?
                                        <TableCell sortColumn="totalCarts">
                                            <FormattedMessage id="departTable.carts" defaultMessage="Carts"/>
                                        </TableCell> : null
                                    }
                                    <TableCell sortColumn="stationCode">
                                        <FormattedMessage id="departTable.station" defaultMessage="Station"/>
                                    </TableCell>
                                    {isNotSSDAndINRegion(configViewModel.capability) ?
                                        <TableCell/> : null
                                    }
                                </TableRow>
                                <TableRow>
                                    {isNotINRegionOrSSD(configViewModel.capability) ?
                                    <TableCell>
                                        <Input alignmentHorizontal="right" type="text" size="small" value={slaFilter}
                                               onChange={setSLAFilter}/>
                                    </TableCell> : <TableCell></TableCell>
                                    }
                                    {isNotSSDAndINRegion(configViewModel.capability) ?
                                        <TableCell>
                                            <Input alignmentHorizontal="right" type="text" size="small"
                                                   value={departByTimeFilter}
                                                   onChange={setDepartByTimeFilter}/>
                                        </TableCell> : null
                                    }
                                    <TableCell>
                                        <Input type="text" size="small" value={routeCodeFilter}
                                               onChange={setRouteCodeFilter}/>
                                    </TableCell>
                                    {isNotINRegionOrSSD(configViewModel.capability) ?
                                    <TableCell>
                                        <Input type="text" size="small" value={locationFilter}
                                               onChange={setLocationFilter}/>
                                    </TableCell> : <TableCell>
                                            <Input type="text" size="small" value={sectorsFilter}
                                                   onChange={setSectorsFilter}/>
                                        </TableCell>
                                    }
                                    <TableCell>
                                        <Input type="text" size="small" value={statusFilter}
                                               onChange={setStatusFilter}/>
                                    </TableCell>
                                    <TableCell alignmentHorizontal="right">
                                        <Input type="text" size="small" value={progressFilter}
                                               onChange={setProgressFilter}/>
                                    </TableCell>
                                    <TableCell>
                                        <Input type="text" size="small" value={typeFilter} onChange={setTypeFilter}/>
                                    </TableCell>
                                    <TableCell>
                                        <Input type="text" size="small" value={driverFilter}
                                               onChange={setDriverFilter}/>
                                    </TableCell>
                                    {!(isARorSSD(configViewModelRef.current.capability) || getRegion() === REGION.IN) && !configViewModel.stationConfig.amxl ?
                                        <TableCell>
                                            <Input type="text" size="small" value={cartsFilter}
                                                   onChange={setCartsFilter}/>
                                        </TableCell> : null}
                                    <TableCell>
                                        <Input type="text" size="small" value={stationCodeFilter}
                                               onChange={setStationCodeFilter}/>
                                    </TableCell>
                                    {isNotSSDAndINRegion(configViewModel.capability) ?
                                        <TableCell/>
                                         : null
                                    }
                                </TableRow>
                                {
                                    routeList.map((route, key) => (

                                        (key >= startPage && key <= endPage) ?
                                            <TableRow key={route.routeId}>
                                                {isNotINRegionOrSSD(configViewModel.capability) ?
                                                    <TableCell alignmentHorizontal="right">{route.routeSLA}</TableCell>
                                                    :<TableCell>{isRouteReadyForDispatch(route) ? <Checkbox checked={isChecked(route.routeId)} onChange={(e) => checkRow(route.routeId)}/> : null}</TableCell>
                                                }
                                                {isNotSSDAndINRegion(configViewModel.capability) ?
                                                    <TableCell alignmentHorizontal="right">{route.departByTime}</TableCell>
                                                    :null
                                                }
                                                <TableCell>
                                                    <Link onClick={() => {
                                                        onRouteClick(route)
                                                    }} type="secondary">{route.routeCode}</Link>
                                                </TableCell>
                                                {isNotINRegionOrSSD(configViewModel.capability) ?
                                                    <TableCell>
                                                        <Tooltip position={"top"} title={route.stagingLocation}>
                                                            <Text>{trimColValue(route.stagingLocation, 16)}</Text>
                                                        </Tooltip>
                                                    </TableCell> : <TableCell>
                                                        <Tooltip position={"top"} title={route.sectorCodes}>
                                                            <Text>{trimColValue(route.sectorCodes, 50)}</Text>
                                                        </Tooltip>
                                                    </TableCell>
                                                }
                                                <TableCell>
                                                    <Tooltip position="top" title={route.status}>
                                                        <Tag
                                                            type={getDepartStatusColor(route.status)}>{route.status.length > 11 ?
                                                            route.status.substr(0, 10) + "..." : route.status}</Tag>
                                                    </Tooltip>
                                                </TableCell>
                                                {isNotINRegionOrSSD(configViewModel.capability) ?
                                                    <TableCell
                                                        alignmentHorizontal="right">{route.departProgress}</TableCell>
                                                    : <TableCell
                                                        alignmentHorizontal="right">
                                                        {route.stowProgress}{(route.topOffPackageCount && route.topOffPackageCount !== 0) ? " + " + route.topOffPackageCount : ""}
                                                    </TableCell>
                                                }
                                                <TableCell>{route.routeType}</TableCell>
                                                <TableCell>
                                                    <Tooltip position="top" title={route.driver}>
                                                        <Text truncate={true}>
                                                            {route.driver}
                                                        </Text>
                                                    </Tooltip>
                                                </TableCell>
                                                {isNotINRegionOrSSD(configViewModel.capability) && !configViewModel.stationConfig.amxl ?
                                                <TableCell>
                                                    <Text>
                                                        {route.totalCarts}
                                                    </Text>
                                                </TableCell> : null}
                                                <TableCell>
                                                    <Text>
                                                        {getStation(route.stationCode)}
                                                    </Text>
                                                </TableCell>
                                                <TableCell>
                                                    {isNotSSDAndINRegion(configViewModel.capability) && isRouteReadyForDispatch(route) ?
                                                    <TableCell>
                                                        <RouteContextMenu route={route} />
                                                    </TableCell> : null
                                                    }
                                                </TableCell>
                                            </TableRow> :
                                               null
                                    ))
                                }
                            </Table>
                        </div>
                        <div>
                            {routeList.length > 0 ?
                                <Row width="100%" alignmentHorizontal="center" spacingInset="medium none none">
                                    <Pagination
                                        numberOfPages={pageSize}
                                        onChange={setCurrentPage}
                                        currentPage={currentPage}/>
                                </Row> : null}
                        </div>
                    </div>
                </Row>
            </TableScrollWrapper>
        </div>
    )
}

export default DepartTable


// WEBPACK FOOTER //
// ./src/components/depart/DepartTable.js